/* eslint-disable no-use-before-define, @typescript-eslint/no-non-null-assertion */
import { FC, useMemo, useState } from 'react';
import { CurrentRefinements, useHits } from 'react-instantsearch';
import { useScrollDirection } from '../../../shared/use';
import { FoundHits, ResetFilters, useStats } from '../../../shared/components';
import { FilterIcon } from '../../../shared/sections/Filters/icons/Filter';
import { GenericFilter } from '../../../shared/sections/Filters/components/GenericFilter';
import { CollectionFilter } from '../../../shared/sections/Filters/components/CollectionFilter';

type FiltersSectionProps = {
  mobile: boolean;
  displayCollection: boolean;
};

export const FiltersSection: FC<FiltersSectionProps> = ({ mobile, displayCollection }) => {
  const { results } = useHits();

  const newFacetAttributes = results?.renderingContent?.facetOrdering?.facets?.order || ['vendor', 'price'];

  const newFilters = useMemo(() => getNewFilters(newFacetAttributes), [newFacetAttributes]);

  if (displayCollection && !newFilters.includes('collections')) {
    newFilters.unshift('collections');
  }

  if (mobile) {
    return <FiltersSectionMobile newFilters={newFilters} />;
  }

  return (
    <div className="sr-filters-container">
      {newFilters.map((attribute, index) => {
        if (attribute === 'collections') {
          return <CollectionFilter key={attribute} />;
        }

        return <GenericFilter key={attribute} attribute={attribute} sorterValue={index + 1} />;
      })}
    </div>
  );
};

const getNewFilters = (newFacetAttributes: Array<string>): Array<string> => {
  // Filter out collections
  let filtersWithoutCollections = newFacetAttributes.filter((attribute) => !attribute.startsWith('collections'));

  // Add Availability filter
  if (theme.featureFlags.showAvailabilityFilter) {
    const vendorIndex = filtersWithoutCollections.findIndex((attr) => attr === 'vendor');

    if (vendorIndex !== -1) {
      filtersWithoutCollections = [
        ...filtersWithoutCollections.slice(0, vendorIndex),
        'availability',
        ...filtersWithoutCollections.slice(vendorIndex),
      ];
    } else {
      filtersWithoutCollections.push('availability');
    }
  }

  return filtersWithoutCollections;
};

type FiltersSectionMobileProps = {
  newFilters: Array<string>;
};

export const FiltersSectionMobile: FC<FiltersSectionMobileProps> = ({ newFilters }) => {
  const scrollDirection = useScrollDirection();
  const [showFilters, setShowFilters] = useState(false);
  const { nbHits } = useStats();

  return (
    <>
      <div
        className="sr-mb-filters-container"
        style={{ top: scrollDirection === 'down' || showFilters ? 0 : 'var(--top-header-height, 85px)' }}
      >
        <FoundHits />

        <div>
          <button className="filter-button" onClick={() => setShowFilters(!showFilters)}>
            <FilterIcon />
            {theme.locales.filterLabel}
          </button>
        </div>

        {/* current refinements should be exist in DOM so that user can reset them */}
        <CurrentRefinements style={{ display: 'none' }} />

        <ResetFilters />
      </div>

      <div style={{ display: showFilters ? 'block' : 'none' }}>
        <div className="sr-mb-filters-wrapper" style={{ top: '35px' }}>
          {newFilters.map((attribute, index) => {
            if (attribute === 'collections') {
              return <CollectionFilter key={attribute} />;
            }

            return <GenericFilter key={attribute} attribute={attribute} sorterValue={index + 1} />;
          })}
        </div>

        <div className="sr-mb-filters-footer">
          <ResetFilters containerClassName="reset-filters-button" />

          <button className="see-results-button" onClick={() => setShowFilters(false)}>
            {theme.locales.seeAmountOfResult.replace('[amount]', Number(nbHits).toLocaleString())}
          </button>
        </div>
      </div>
    </>
  );
};
